import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { AlertIcon, PlugIcon } from "./Icons.js";
import { Button } from "./Button.js";
export function TopBar({ onMenu, menuOpen, menuLabel, centre, right, scrolled = false, }) {
    return (_jsx("header", { className: ["wr-topbar", scrolled ? "wr-topbar--scrolled" : ""].filter(Boolean).join(" "), "data-testid": "top-bar", children: _jsxs("div", { className: "wr-topbar__inner", children: [_jsx("button", { type: "button", className: "wr-icon-btn", "aria-label": menuLabel, "aria-expanded": menuOpen, "data-testid": "hamburger", onClick: onMenu, children: _jsx("span", { className: "wr-icon-btn__visual", children: _jsxs("span", { className: ["wr-burger", menuOpen ? "wr-burger--open" : ""].filter(Boolean).join(" "), "aria-hidden": "true", children: [_jsx("span", { className: "wr-burger__bar" }), _jsx("span", { className: "wr-burger__bar" }), _jsx("span", { className: "wr-burger__bar" })] }) }) }), _jsx("div", { className: "wr-topbar__centre", children: centre }), _jsx("div", { className: "wr-topbar__right", children: right })] }) }));
}
/** Tracks whether the page has scrolled at all — §10 `scrolled` state. */
export function useScrolled(threshold = 4) {
    const [scrolled, setScrolled] = useState(false);
    useEffect(() => {
        function onScroll() {
            setScrolled(window.scrollY > threshold);
        }
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, [threshold]);
    return scrolled;
}
/** LOCKED (§13.1): 600ms debounce on `reconnecting`; `offline` mounts at once. */
const RECONNECT_DEBOUNCE_MS = 600;
const RESTORED_MS = 1600;
export function ConnectionBanner({ state, message, action }) {
    const [visible, setVisible] = useState(state === "offline" || state === "browser-offline");
    useEffect(() => {
        if (state === "online") {
            setVisible(false);
            return;
        }
        if (state === "reconnecting") {
            // A one-frame blip on a bus must not flash a warning bar.
            const timer = setTimeout(() => setVisible(true), RECONNECT_DEBOUNCE_MS);
            return () => clearTimeout(timer);
        }
        if (state === "restored") {
            setVisible(true);
            const timer = setTimeout(() => setVisible(false), RESTORED_MS);
            return () => clearTimeout(timer);
        }
        setVisible(true);
        return;
    }, [state]);
    if (!visible || state === "online")
        return null;
    const tone = state === "reconnecting" ? "reconnecting" : state === "restored" ? "restored" : "offline";
    return (_jsxs("div", { className: `wr-conn-banner wr-conn-banner--${tone}`, role: "status", "aria-live": "polite", "data-testid": "connection-banner", "data-state": state, children: [_jsx("span", { "aria-hidden": "true", children: state === "reconnecting" ? _jsx(PlugIcon, { size: 16 }) : _jsx(AlertIcon, { size: 16 }) }), _jsxs("span", { children: [message, state === "reconnecting" ? _jsx("span", { className: "wr-dots", "aria-hidden": "true" }) : null] }), action ? (_jsx("span", { className: "wr-conn-banner__action", children: _jsx(Button, { variant: "secondary", size: "sm", onClick: action.onAction, "data-testid": "connection-retry", children: action.label }) })) : null] }));
}
/**
 * The content-area panel that replaces a dead room render (§13.2).
 *
 * This is the **only** recovery control in that viewport. The connection banner
 * carries none, and when the socket is healthy the banner does not render at
 * all — saying "your connection dropped" when it did not is a lie the player
 * can check.
 */
export function SeatReleasedPanel({ title, body, actionLabel, onAction, disabledReason, testId, }) {
    return (_jsxs("section", { className: "wr-seat-panel", "data-testid": testId ?? "seat-lost", role: "status", children: [_jsx("span", { className: "wr-seat-panel__glyph", "aria-hidden": "true", children: _jsx(AlertIcon, { size: 20 }) }), _jsx("h2", { className: "wr-display-sm", children: title }), _jsx("p", { className: "wr-body-md wr-ink-70", children: body }), _jsx(Button, { variant: "primary", size: "lg", fullWidth: true, disabled: disabledReason !== undefined, onClick: onAction, "data-testid": "join", children: actionLabel }), disabledReason ? _jsx("p", { className: "wr-mono-sm wr-ink-70", children: disabledReason }) : null] }));
}
/** Raised card, radius xl, 24px padding, max-width 420 (§4). No outline (§2). */
export function AuthCard({ children, testId }) {
    return (_jsx("section", { className: "wr-auth-card", "data-testid": testId ?? "auth-card", children: children }));
}
/** The deferred-body screens (Settings, Profile) — §11, §19 question 2. */
export function StubPanel({ title, body, testId }) {
    return (_jsxs("section", { className: "wr-stub-panel", "data-testid": testId ?? "stub-panel", children: [_jsx("h1", { className: "wr-display-md", "data-route-heading": true, children: title }), _jsx("p", { className: "wr-body-md wr-ink-70", children: body })] }));
}
//# sourceMappingURL=Chrome.js.map